/** ******************************************************************************************
 *                                                                                           *
 *            This page should adhere to the web-preauth LP style guide:                     *
 *                                                                                           *
 * https://github.com/NewAmsterdamLabs/web-preauth/wiki/Pre-auth-Landing-Page-Style-Guide    *
 *                                                                                           *
 ****************************************************************************************** */

import dynamic from 'next/dynamic';
import React from 'react';
import { GetServerSideProps } from 'next';
import ReadyWhenYouAre from '@zola/zola-ui/src/components/PreAuth/ReadyWhenYouAre';
import useForceUpdateOnWindowResize from '@zola/zola-ui/src/hooks/useForceUpdateOnWindowResize';
import type { WStoreNavView } from '@zola/svc-web-api-ts-client';
import NAV_DATA from '@zola-helpers/client/dist/es/constants/navConstants';

// Hooks
import { useIsPostAuthNav3MobileOrDesktop } from '~/hooks/useIsPostAuthNav3Active';

// Helpers
import { fetchNavHTML, fetchShopCategories } from '~/libs/server/nav';
import { fetchUser } from '~/libs/server/users';
import { UserContext } from '@zola-helpers/client/dist/es/@types';
import { ServerResponse } from 'http';
import type IncomingMessageWithCookies from '~/types/IncomingMessageWithCookies';

// Shared components
import Nav from '~/components/Nav';

// Registry Benefits specific components
import Header from '~/components/LP/WeddingRegistryBenefits/Header';
import Hero from '~/components/LP/WeddingRegistryBenefits/Hero';
import AllYourGiftsImageSection from '~/components/LP/WeddingRegistryBenefits/AllYourGiftsImageSection';
import FreeShippingImageSection from '~/components/LP/WeddingRegistryBenefits/FreeShippingImageSection';
import FiveStarImageSection from '~/components/LP/WeddingRegistryBenefits/FiveStarImageSection';
import ThankYouNoteImageSection from '~/components/LP/WeddingRegistryBenefits/ThankYouNoteImageSection';
import CashFundDiscountCardsSection from '~/components/LP/WeddingRegistryBenefits/CashFundDiscountCardsSection';
import RegistryCardsSection from '~/components/LP/WeddingRegistryBenefits/RegistryCardsSection';
import StreamlinePlanningSection from '~/components/LP/WeddingRegistryBenefits/StreamlinePlanningSection';
import BrandsCarouselSection from '~/components/LP/WeddingRegistryBenefits/BrandsCarouselSection';
import WorriedSection from '~/components/LP/WeddingRegistryBenefits/WorriedSection';

// Hooks
import { SecondaryNavType } from '~/components/Nav/types';

// Styles
import styles from './benefits.module.less';

const Footer = dynamic(import('~/components/ui/Footer/Footer'), {
  ssr: false,
});

type RegistryBenefitsLPProps = {
  navHTML: string | null;
  userCtx: UserContext | null;
  shopCategories: WStoreNavView;
};

const RegistryBenefitsLPContainer: React.FC<RegistryBenefitsLPProps> = ({
  navHTML,
  userCtx,
  shopCategories,
}) => {
  useForceUpdateOnWindowResize();
  const isPostAuthNav3Enabled = useIsPostAuthNav3MobileOrDesktop(userCtx);

  const handleCTAClick = () => {
    const hasRegistry = userCtx && userCtx.has_registry;
    if (hasRegistry) {
      document.location.href = '/registry/overview';
    } else {
      document.location.href = '/onboard/new';
    }
  };

  return (
    <>
      <Header />
      <Nav
        navHTML={navHTML}
        showPromo
        user={userCtx}
        disablePrimaryNavCollapse
        activePrimaryLink={NAV_DATA.PRIMARY.REGISTRY}
        shopCategories={shopCategories}
        secondaryNavType={SecondaryNavType.REGISTRY}
      />
      <div className={isPostAuthNav3Enabled ? styles.isPostAuthNav3Enabled : ''}>
        <Hero onCTAClick={handleCTAClick} />
        <AllYourGiftsImageSection />
        <FreeShippingImageSection />
        <CashFundDiscountCardsSection />
        <FiveStarImageSection />
        <ThankYouNoteImageSection />
        <RegistryCardsSection />
        <StreamlinePlanningSection />
        <BrandsCarouselSection />
        <div>
          <WorriedSection />
        </div>
        <ReadyWhenYouAre
          text={
            <>
              <span className={styles.rose}>Ready</span> <br className={styles.hideDesktop} />
              To Get Started?
            </>
          }
          onClick={handleCTAClick}
          buttonText="Let's do this"
          titleElement="h2"
        />
        <Footer />
      </div>
    </>
  );
};

export default RegistryBenefitsLPContainer;

export const getServerSideProps: GetServerSideProps = async ({
  req,
}: {
  req: IncomingMessageWithCookies;
  res: ServerResponse;
}) => {
  const [navHTML, userCtx, shopCategories] = await Promise.all([
    fetchNavHTML(req),
    fetchUser(req),
    fetchShopCategories(),
  ]);

  return {
    props: {
      navHTML,
      userCtx,
      shopCategories,
    },
  };
};
